// ts-rest contract for the client clarification of a locked fact find
// (SEP-293 section 7).
//
// Endpoints:
//   GET  /api/desk/fact-find/clarifications/:clientId — the requests of a client
//   POST /api/desk/fact-find/clarifications/:clientId — the IM asks the client
//   GET  /api/portal/clarifications                   — the requests of the portal client
//   POST /api/portal/clarifications/:requestId/answer — the client answers
//
// The IM names the questions and writes a message. The IM never writes an
// answer: the client answers in the portal, and each answer becomes an
// evidenced amendment of the fact find.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { errorBodyWithCode as errorBody } from '../schemas/common';

const c = initContract();

const answerSchema = z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]);

export const clarificationQuestionSchema = z.object({
    sectionId: z.string(),
    questionId: z.string(),
    label: z.string(),
    originalValue: answerSchema,
    /** The answer of the client, once the request is answered. */
    answer: answerSchema,
});

export const clarificationRequestSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    versionId: z.string(),
    flagId: z.string().nullable(),
    message: z.string(),
    status: z.enum(['open', 'answered', 'cancelled']),
    requestedByName: z.string().nullable(),
    requestedAt: z.string(),
    answeredAt: z.string().nullable(),
    questions: z.array(clarificationQuestionSchema),
});
export type ClarificationRequestDto = z.infer<typeof clarificationRequestSchema>;

const listSchema = z.object({ requests: z.array(clarificationRequestSchema) });

export const factFindClarificationContract = c.router(
    {
        listForClient: {
            method: 'GET',
            path: '/desk/fact-find/clarifications/:clientId',
            responses: { 200: listSchema, 404: errorBody },
            summary: 'The clarification requests of one client (SEP-293 section 7)',
        },
        request: {
            method: 'POST',
            path: '/desk/fact-find/clarifications/:clientId',
            body: z.object({
                /** The ids of the fact-find questions to clarify. */
                questionIds: z.array(z.string().min(1)).min(1),
                message: z.string().trim().min(1).max(4000),
                flagId: z.string().nullable().optional(),
            }),
            responses: { 200: listSchema, 400: errorBody, 403: errorBody, 404: errorBody },
            summary: 'The IM asks the client to clarify answers of the locked fact find',
        },
        portalList: {
            method: 'GET',
            path: '/portal/clarifications',
            responses: { 200: listSchema, 401: errorBody },
            summary: 'The clarification requests of the signed-in portal client',
        },
        portalAnswer: {
            method: 'POST',
            path: '/portal/clarifications/:requestId/answer',
            body: z.object({
                answers: z.record(
                    z.string(),
                    z.union([z.string().trim().min(1), z.array(z.string())]),
                ),
            }),
            responses: { 200: listSchema, 400: errorBody, 401: errorBody, 404: errorBody },
            summary: 'The portal client answers one clarification request',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
